import type { ReactNode } from "react";

import { Card } from "./Card.js";
import { classes } from "./classes.js";

/**
 * `P0-UIK-001`. A figure with its label, at 4.6.3's `text-2xl` for figures.
 *
 * **`value` is a `ReactNode`, deliberately, and not a number.** A number prop
 * would invite `<StatCard value={1500000} />` and this component would then be
 * the one place in the product that formats money , which is `formatMoney`'s
 * job. Taking a node makes `<StatCard value={<Money amount={x} />} />` the
 * natural call, and `hakili/no-raw-figure` catches the alternative.
 */
export interface StatCardProps {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly hint?: ReactNode;
  /** `F-0b`: the bank's tile states a thing that needs a hand (`hot`) or is settled (`ok`); absent, a plain tile. */
  readonly tone?: "hot" | "ok";
  readonly className?: string;
}

export function StatCard({ label, value, hint, tone, className }: StatCardProps): React.JSX.Element {
  return (
    <Card className={classes("flex flex-col gap-2", className)} dataKit="stat" dataTone={tone}>
      <span className="text-xs text-gray-500">{label}</span>
      <span data-kit="stat-value" className="text-2xl font-semibold text-gray-900">
        {value}
      </span>
      {hint === undefined ? null : (
        <span data-kit="stat-hint" className="text-xs text-gray-400">
          {hint}
        </span>
      )}
    </Card>
  );
}
